import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useParams, Link, useNavigate } from 'react-router-dom';
import { FileText, Settings, Trash2, Plus, ArrowLeft, Users } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './FolderDetail.scss';

interface SelectionFolder {
  id: number;
  user_id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  selections_count: number;
}

interface Selection {
  id: number;
  user_id: number;
  folder_id?: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  candidates_count: number;
  folder_name?: string;
}

/**
 * FolderDetail Page
 * 
 * Shows all selections (atrankos) within a specific folder (aplankas).
 * Allows creating new selections within the folder and managing existing ones.
 */
const FolderDetail: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [folder, setFolder] = useState<SelectionFolder | null>(null);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [editingSelection, setEditingSelection] = useState<Selection | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Form states
  const [selectionName, setSelectionName] = useState('');
  const [selectionDescription, setSelectionDescription] = useState('');

  const fetchFolder = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/folders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setFolder(result.data);
      }
    } catch (error) {
      console.error('Error fetching folder:', error);
    }
  }, [id]);

  const fetchSelections = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/folders/${id}/selections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSelections(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching selections:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer' && id) {
      fetchFolder();
      fetchSelections();
    }
  }, [isAuthenticated, user, id, fetchFolder, fetchSelections]);

  const handleCreateSelection = () => {
    setEditingSelection(null);
    setSelectionName('');
    setSelectionDescription('');
    setShowSelectionModal(true);
  };

  const handleEditSelection = (selection: Selection) => {
    setEditingSelection(selection);
    setSelectionName(selection.name);
    setSelectionDescription(selection.description || '');
    setShowSelectionModal(true);
  };

  const handleSaveSelection = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const url = editingSelection
        ? `${API_URL}/employer-selections/selections/${editingSelection.id}`
        : `${API_URL}/employer-selections/selections`;
      
      const method = editingSelection ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: selectionName,
          description: selectionDescription,
          folder_id: parseInt(id!)
        })
      });

      if (response.ok) {
        setShowSelectionModal(false);
        fetchSelections();
        fetchFolder(); // Update folder count
      }
    } catch (error) {
      console.error('Error saving selection:', error);
    }
  };

  const handleDeleteSelection = async (selectionId: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šią atranką?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/selections/${selectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchSelections();
        fetchFolder(); // Update folder count
      }
    } catch (error) {
      console.error('Error deleting selection:', error);
    }
  };

  const handleViewCandidates = (selectionId: number) => {
    navigate(`/atrankos/selection/${selectionId}`);
  };

  const toggleSelectItem = (selectionId: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(selectionId)) {
      newSelected.delete(selectionId);
    } else {
      newSelected.add(selectionId);
    }
    setSelectedItems(newSelected);
    setSelectAll(newSelected.size === selections.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
      setSelectAll(false);
    } else {
      setSelectedItems(new Set(selections.map(s => s.id!)));
      setSelectAll(true);
    }
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="folder-detail-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect non-employers
  if (!isAuthenticated || user?.user_type !== 'employer') {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div className="folder-detail-page">
        <div className="container">
          <div className="loading">Kraunama...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="folder-detail-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Titulinis</Link>
          <span>&gt;</span>
          <Link to="/atrankos">Atrankos</Link>
          <span>&gt;</span>
          <span className="current">{folder?.name}</span>
        </div>

        <div className="page-header">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/atrankos')}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1>{folder?.name}</h1>
              {folder?.description && <p className="subtitle">{folder.description}</p>}
            </div>
          </div>
          <button className="btn-create" onClick={handleCreateSelection}>
            <Plus size={20} />
            Nauja atranka
          </button>
        </div>

        <div className="filters-bar">
          <div className="filter-left">
            <button className="btn-filter active">Rodyti atrankos</button>
            <button className="btn-select-all" onClick={handleSelectAll}>
              {selectAll ? '✓' : ''} Žymėti visus
            </button>
            <select className="select-action" disabled={selectedItems.size === 0}>
              <option value="">Pasirinkti veiksmą</option>
              <option value="delete">Ištrinti pažymėtas</option>
            </select>
          </div>
        </div>

        {selections.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} />
            <p>Šiame aplanke dar nėra atranką</p>
            <button className="btn-create-empty" onClick={handleCreateSelection}>
              <Plus size={20} />
              Sukurti pirmą atranką
            </button>
          </div>
        ) : (
          <div className="selections-grid">
            {selections.map((selection) => (
              <div key={selection.id} className="selection-card">
                <div className="card-header">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(selection.id!)}
                    onChange={() => toggleSelectItem(selection.id!)}
                    className="selection-checkbox"
                  />
                  <div className="card-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleEditSelection(selection)}
                      title="Redaguoti"
                    >
                      <Settings size={18} />
                    </button>
                    <button 
                      className="btn-icon btn-delete"
                      onClick={() => handleDeleteSelection(selection.id!)}
                      title="Ištrinti"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <h3 className="card-title">{selection.name}</h3>
                  {selection.description && (
                    <p className="card-description">{selection.description}</p>
                  )}
                  <div className="card-info">
                    <Users size={16} />
                    <span>Kandidatų skaičius: {selection.candidates_count}</span>
                  </div>
                  <div className="card-status">
                    <span className="status-badge">Atranka yra aktyvi</span>
                  </div>
                </div>

                <button 
                  className="btn-view-candidates"
                  onClick={() => handleViewCandidates(selection.id!)}
                >
                  PERŽIŪRĖTI KANDIDATUS
                  <span className="arrow">→</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Selection Modal */}
        {showSelectionModal && (
          <div className="modal-overlay" onClick={() => setShowSelectionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingSelection ? 'Redaguoti atranką' : 'Nauja atranka'}</h2>
                <button className="btn-close" onClick={() => setShowSelectionModal(false)}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="selectionName">Pavadinimas *</label>
                  <input
                    type="text"
                    id="selectionName"
                    value={selectionName}
                    onChange={(e) => setSelectionName(e.target.value)}
                    placeholder="pvz., Frontend Developer pozicija"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="selectionDescription">Aprašymas</label>
                  <textarea
                    id="selectionDescription"
                    value={selectionDescription}
                    onChange={(e) => setSelectionDescription(e.target.value)}
                    placeholder="Trumpas aprašymas apie šią atranką"
                    rows={3}
                  />
                </div>

                <div className="info-note">
                  <strong>Aplankas:</strong> {folder?.name}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowSelectionModal(false)}>
                  Atšaukti
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSaveSelection}
                  disabled={!selectionName.trim()}
                >
                  {editingSelection ? 'Atnaujinti' : 'Sukurti'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderDetail;

